<template>
  <div class="px-10 flex flex-col h-full pb-10">
    <p class="font-bold text-pink-500 text-center text-4xl mt-[2px] mb-3 font-[family-name:Red_Hat_Display]"><span class="currencyCode">$</span><span id="pageThree-Amount">{{donationAmountForView}}</span></p>
    <div class="space-y-3"> 
      <fieldset>
        <label for="credit-card" class="block text-base text-gray-700 font-medium">Card Details</label>
        <div class="relative my-1">
          <div ref="cardElementContainer" class="block w-full sm:text-sm py-2 px-2 r-10 text-base focus:ring-0 placeholder:text-gray-200 border rounded-md" :class="{ 'border-gray-300': isCardElementFocused }">
            <div ref="cardElement"></div>
          </div>
        </div>
      </fieldset>
      <!-- Employer-match search promoted to its own "Double Your Impact"
           step — see EmployerMatchForm.vue. Removed from PaymentForm so
           the card-entry moment is uncluttered. -->
      <fieldset class="space-y-0 mt-2">
        <button
          @click.prevent="next"
          :disabled="isLoading || previewWallet"
          class="transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] bg-pink-500 hover:bg-pink-700 active:scale-[0.98] disabled:active:scale-100 disabled:opacity-70 disabled:cursor-wait text-white uppercase py-2 px-4 w-full font-bold rounded-full inline-flex items-center justify-center gap-2 font-[family-name:Red_Hat_Display] tracking-wide text-lg"
        >
          <svg v-if="isLoading" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span v-if="isLoading">Processing…</span>
          <span v-else>Donate ${{ donationAmountForView }}</span>
        </button>
      </fieldset>
      <!-- Trust panel below the Donate button. SECURE CHECKOUT lockup
           (blue palette) carries the right-column trust payload; the
           three trust pillars live in the left hero panel (see
           MakeDonationWidget.vue) so the wallet block can stay minimal:
           an em-dashed "— or pay with —" divider above the Stripe
           paymentRequest mount, which only surfaces when Apple Pay /
           Google Pay / Link is actually available in the browser. -->
      <div
        class="trust-panel"
        aria-label="Wallet payment options"
      >
        <div class="trust-panel__lockup">
          <span class="trust-panel__eyebrow">
            <svg class="trust-panel__eyebrow-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Secure Checkout
          </span>
          <span class="trust-panel__cards" aria-label="We accept Visa, Mastercard, American Express, and Discover">
            <img src="/images/visa.svg" alt="" class="trust-panel__card">
            <img src="/images/mastercard.svg" alt="" class="trust-panel__card">
            <img src="/images/amex.svg" alt="" class="trust-panel__card">
            <img src="/images/discover.svg" alt="" class="trust-panel__card">
          </span>
        </div>
        <div v-if="hasPaymentRequest" class="wallet-divider-slim" aria-hidden="true">— or pay with —</div>
        <div
          id="payment-request-button"
          class="trust-panel__wallet-mount"
          :class="{ 'is-active': hasPaymentRequest }"
        >
          <button
            v-if="previewWallet"
            type="button"
            class="trust-panel__wallet-preview-button"
            tabindex="-1"
            aria-hidden="true"
          >
            <span class="trust-panel__wallet-preview-label">
              Donate with <span class="trust-panel__wallet-preview-g">G</span> Pay
            </span>
          </button>
        </div>
      </div>
    </div>
    
    <fieldset class="dw-back-row mt-auto flex justify-center">
      <button
        @click.prevent="back"
        type="button"
        class="dw-back-pill dw-back-pill--lg"
        aria-label="Back to previous step"
      >
        <svg class="dw-back-pill__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
        <span>Back</span>
      </button>
    </fieldset>
  </div>
</template>

<script>
import { StripeElements, StripeElementCard } from "@vue-stripe/vue-stripe";
import { loadStripe } from "@stripe/stripe-js";
import mitt from 'mitt';
import BigNumber from 'bignumber.js';

const emitter = mitt();

export default {
  components: {
    StripeElementCard
  },
  data() {
    return {
      stripe: null,
      cardElement: null,
      cardError: null,
      isLoading: false,
      isCardElementFocused: false,
      // Toggles the "Other Payment Methods" label. Flipped to true only
      // once Stripe's paymentRequest.canMakePayment() resolves truthy —
      // i.e. Apple Pay, Google Pay, or Link is actually available in
      // this browser. Without this, the label shows above an empty div.
      hasPaymentRequest: false,
      // Dev-only flag, set in mounted() when ?wallet=preview is in the
      // URL. Renders a static placeholder shaped like Stripe's wallet
      // button so spacing can be tuned without a real Google Pay
      // account. Never set true in production paths.
      previewWallet: false,
    };
  },
  async mounted() {
    // Dev-only wallet preview — gated by ?wallet=preview. Forces the
    // wallet section visible (placeholder button rendered via v-if
    // below) and skips the real Stripe paymentRequest boot. The card
    // element still mounts normally so the rest of Step 2's layout is
    // intact for spacing QA.
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('wallet') === 'preview') {
        this.previewWallet = true;
        this.hasPaymentRequest = true;
      }
    }

    // DoubleTheDonation init moved to EmployerMatchForm.vue
    // Resolve the Stripe publishable key at RUNTIME from a <meta> tag the
    // layout renders ({{ config('services.stripe.key') }}). Build-time
    // resolution via process.env.MIX_STRIPE_PUBLISHABLE_KEY bakes whichever
    // key was set in the .env on the build machine into the bundle, which
    // means a bundle built locally with one Stripe account's pk and
    // deployed to a server using a different account's sk produces
    // "No such PaymentMethod" failures on charge. The meta-tag approach
    // ships an environment-independent bundle and pulls the right key per
    // environment, exactly like the CSRF token already does.
    const stripeKeyMeta = document.querySelector('meta[name="stripe-publishable-key"]')
    const stripeKey = (stripeKeyMeta && stripeKeyMeta.getAttribute('content'))
      || process.env.MIX_STRIPE_PUBLISHABLE_KEY
      || ''
    if (!stripeKey) {
      console.error('Stripe publishable key not found in meta[name="stripe-publishable-key"] or MIX_STRIPE_PUBLISHABLE_KEY env')
    }
    this.stripe = await loadStripe(stripeKey);

    // paymentRequest / wallet button setup — skipped in preview mode
    // since the placeholder above takes the wallet slot, and mounting
    // a real Stripe prButton over it would either overlap visually
    // or call canMakePayment() and hide the parent div via display:none.
    if (!this.previewWallet) {
      const paymentRequest = this.stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Donation",
          amount: Math.round(this.calculatedTotal.toFixed(0)),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      const prButton = this.stripe.elements().create("paymentRequestButton", {
        paymentRequest: paymentRequest,
        style: {
          paymentRequestButton: {
            type: "donate",
            theme: "dark",
            height: "40px",
            padding: {
              top: "10px",
              bottom: "10px",
              left: "12px",
              right: "12px"
            },
            borderRadius: "9999px"
          }
        }
      });

      paymentRequest.canMakePayment().then((result) => {
        if (result) {
          prButton.mount("#payment-request-button");
          this.hasPaymentRequest = true;
        } else {
          document.getElementById("payment-request-button").style.display = "none";
        }
      });

      paymentRequest.on("paymentmethod", async (event) => {
        // Handle the payment request submission here. We pass the full
        // event (not just event.paymentMethod) so the downstream submit
        // can fall back on event.payerName / event.payerEmail when the
        // wallet's billing_details come back empty — Google Pay on
        // Android in particular returns name+email at the EVENT level
        // but leaves billing_details.name blank.
        const { paymentMethod } = event;
        console.log('Payment Method received:', paymentMethod);
        console.log('Payment Method type:', paymentMethod.type);
        console.log('Event payerName/payerEmail:', event.payerName, event.payerEmail);
        if (paymentMethod.card) {
          console.log('Card details:', paymentMethod.card);
          if (paymentMethod.card.wallet) {
            console.log('Wallet details:', paymentMethod.card.wallet);
            console.log('Wallet type:', paymentMethod.card.wallet.type);
          }
        }
        await this.submitDigitalWalletPayment(event);
        event.complete("success");
      });
    }

    this.cardElement = this.stripe.elements().create("card", {
      classes: {
        invalid: 'border-red-500',
        complete: 'border-green-500',
      },
      style: {
        base: {
          fontSize: '16px',
          color: '#32325d',
        },
      },
    });
    this.cardElement.mount(this.$refs.cardElement);
    
    // Stripe's card element shows its own inline red validation
     // message under the input on every change; we don't also toast
    // those — that was stacking "Your card number is incomplete"
    // once per keystroke into an unclosable pile. Error toasts now
    // only fire on Donate-button click (handleCard / next), so a
    // user who's still typing doesn't get spammed.

    this.cardElement.addEventListener('focus', () => {
      this.isCardElementFocused = true;
    });
    
    this.cardElement.addEventListener('blur', () => {
      this.isCardElementFocused = false;
    });
  },
  computed: {
    donationWidgetState() {
      return this.$store.getters.donationWidget
    },
    calculatedFee() {
      const fixedFee = '30' // in cents
      const percentageFee = new BigNumber(0.022) // percentage
      const one = new BigNumber(1)
      const goal = new BigNumber(this.donationWidgetState.amount)
      const calculatedCharge = goal.plus(fixedFee).dividedBy(one.minus(percentageFee))
      return BigNumber(calculatedCharge.minus(goal))
    },
    
    calculatedTotal() {
      const amount = new BigNumber(this.donationWidgetState.amount)
      if (this.donationWidgetState.paidForTransactionFee) {
        return BigNumber(amount.plus(this.calculatedFee))
      }
      return amount
    },
    
    donationAmountForView() {
      return new BigNumber(this.calculatedTotal).dividedBy(100).toFormat(2)
    },
  },
  methods: {
    back() {
      this.donationWidgetState.enterFromDirection = '-translate-x-full'
      this.donationWidgetState.leaveToDirection = 'translate-x-full'
      
      this.donationWidgetState.steps.payment.isInView = false
      this.donationWidgetState.steps.donation.isInView = true
      return
    },
    async handleCard() {
      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement
      });

      if (error) {
        // Show error to user
        this.$notify({
          text: error.message || 'Card validation failed. Please check your card details.',
          type: 'error',
          group: 'donationWidget',
        });
        console.error(error);
        this.paymentMethod = null;
      } else {
        // Store the paymentMethod object
        this.paymentMethod = paymentMethod;
      }
    },
    async submitDigitalWalletPayment(eventOrPaymentMethod) {
      this.isLoading = true;

      try {
        // Accept either the full PaymentRequest event (preferred — gives
        // us event.payerName / event.payerEmail) or a bare PaymentMethod
        // object (older call signature). `event.paymentMethod` exists on
        // the event; a bare PM has `.type` at the top level.
        const isEvent = eventOrPaymentMethod && eventOrPaymentMethod.paymentMethod
        const event = isEvent ? eventOrPaymentMethod : null
        const paymentMethod = isEvent ? eventOrPaymentMethod.paymentMethod : eventOrPaymentMethod
        const payerNameFromEvent  = event?.payerName  || ''
        const payerEmailFromEvent = event?.payerEmail || ''

        console.log('Submitting digital wallet payment with payment method:', paymentMethod);

        let detectedWalletType = null;
        if (paymentMethod.type === 'card' && paymentMethod.card?.wallet) {
          // Check for Apple Pay
          if (paymentMethod.card.wallet.apple_pay) {
            detectedWalletType = 'apple_pay';
          }
          // Check for Google Pay
          else if (paymentMethod.card.wallet.google_pay) {
            detectedWalletType = 'google_pay';
          }
          console.log('Detected wallet type:', detectedWalletType);
        } else if (paymentMethod.type === 'link') {
          detectedWalletType = 'link';
          console.log('Detected Stripe Link payment');
        }

        // Safely extract payment type identifier
        let paymentTypeIdentifier = 'unknown';
        if (paymentMethod.card) {
          paymentTypeIdentifier = `${paymentMethod.card.brand || 'card'}-${paymentMethod.card.last4 || '****'}`;
        } else if (paymentMethod.type === 'link') {
          paymentTypeIdentifier = 'stripe-link';
        } else {
          paymentTypeIdentifier = paymentMethod.type || 'unknown';
        }

        // Safely extract billing details with fallbacks.
        //
        // Name + email source-of-truth, in priority order:
        //   1. paymentMethod.billing_details (Apple Pay path)
        //   2. event.payerName / event.payerEmail (Google Pay path — Stripe
        //      surfaces these at the EVENT level when requestPayerName /
        //      requestPayerEmail were set on the paymentRequest config, and
        //      Google Pay often returns them HERE while leaving
        //      billing_details.name empty).
        const billingDetails = paymentMethod.billing_details || {};
        const billingAddress = billingDetails.address || {};
        const resolvedName  = billingDetails.name  || payerNameFromEvent  || '';
        const resolvedEmail = billingDetails.email || payerEmailFromEvent || '';
        const nameParts = resolvedName.split(' ').filter(Boolean);
        const firstName = nameParts[0] || '';
        const lastName  = nameParts.slice(1).join(' ') || '';
        const walletCity  = billingAddress.city || '';
        const walletState = billingAddress.state || '';
        const walletZip   = billingAddress.postal_code || '';

        // Wallets are inconsistent about what they hand back. Apple Pay
        // typically returns a complete billing address; Google Pay (and
        // Stripe Link on some configurations) commonly return name + email
        // and an EMPTY address object. The donation backend requires
        // first_name / last_name / city / state / zip on every POST and
        // 422s if any are missing — that's the exact failure mode Rick hit
        // ("the test card 4242 doesn't work for me"). Detect the gap here
        // and bounce the user through ContactForm to collect what the
        // wallet didn't share, carrying the wallet's tokenized payment
        // method forward so the final POST still pays via the wallet.
        const walletBillingIncomplete = !firstName || !lastName || !walletCity || !walletState || !walletZip;
        if (walletBillingIncomplete) {
          this.donationWidgetState.paymentToken      = paymentMethod.id;
          this.donationWidgetState.paymentIdentifier = paymentTypeIdentifier;
          if (firstName)     this.donationWidgetState.firstName = firstName;
          if (lastName)      this.donationWidgetState.lastName  = lastName;
          if (resolvedEmail) this.donationWidgetState.email     = resolvedEmail;
          if (walletCity)    this.donationWidgetState.city      = walletCity;
          if (walletState)   this.donationWidgetState.state     = walletState;
          if (walletZip)     this.donationWidgetState.zip       = walletZip;

          // Friendly hand-off — the user just tapped a one-click wallet
          // button and is now being asked for more info. Frame as a quick
          // confirmation rather than an error so the donation experience
          // stays positive.
          const walletLabel = detectedWalletType === 'apple_pay' ? 'Apple Pay'
            : detectedWalletType === 'google_pay' ? 'Google Pay'
            : 'your wallet';
          this.$notify({
            text: `${walletLabel} didn't share your billing address. Confirm a couple details below to finish your donation.`,
            type: 'info',
            group: 'donationWidget',
          });

          // Step 2 (payment) → step 4 (contact). Skip employer-match
          // since wallet tap implied a "just complete it" intent — we can
          // still surface employer-match on the thank-you screen if needed.
          this.donationWidgetState.steps.payment.isInView      = false;
          this.donationWidgetState.steps.payment.isValid       = true;
          this.donationWidgetState.steps.contact.isInView      = true;
          this.isLoading = false;
          return;
        }

        const response = await axios.post('/api/donation-widget-payment', {
          team_id: 1,
          campaign_id: this.donationWidgetState.campaignId,
          pledge_id: this.donationWidgetState.pledgeId,
          amount: new BigNumber(this.calculatedTotal).toFixed(0),
          calculated_transaction_fee: this.calculatedFee.toFixed(0),
          paid_for_transaction_fee: this.donationWidgetState.paidForTransactionFee,
          payment_type_id: 1,
          wallet_type: detectedWalletType,
          payment_type_identifier: paymentTypeIdentifier,
          business_name: this.donationWidgetState.businessName,
          address: '',
          is_anonymous: this.donationWidgetState.isAnonymous,
          occurrence: this.donationWidgetState.occurrence,
          double_the_donation: this.donationWidgetState.doubleTheDonation,
          token: paymentMethod.id,
          has_dedication: this.donationWidgetState.hasDedication,
          in_memory_honor_of: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeName : null,
          honoree_name: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeName : null,
          honoree_email: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeEmail : null,
          message_to_recipient: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeMessage : null,
          selection: this.donationWidgetState.hasDedication ? this.donationWidgetState.inHonorOfSelection : null,
          first_name: firstName,
          last_name: lastName,
          email: resolvedEmail,
          city: billingAddress.city || '',
          state: billingAddress.state || '',
          zip: billingAddress.postal_code || '',
        });

        console.log('Payment response:', response.data);
      
        // Handle success
        emitter.emit('campaign-donation-update', response.data.campaign);
        this.donationWidgetState.successfulTransactionData = response.data;
      
        const ddData = {
          '360matchpro_public_key': 'M2JlZmUwZDQtYjRm',
          campaign: 'double-the-donation',
          donation_identifier: this.donationWidgetState.successfulTransactionData.id.toString(),
          donation_amount: this.donationWidgetState.successfulTransactionData.amount.toString(),
          donor_first_name: this.donationWidgetState.successfulTransactionData.first_name,
          donor_last_name: this.donationWidgetState.successfulTransactionData.last_name,
          donor_email: this.donationWidgetState.successfulTransactionData.email,
          doublethedonation_company_id: this.donationWidgetState.successfulTransactionData.double_the_donation.doublethedonation_company_id,
          doublethedonation_status: this.donationWidgetState.successfulTransactionData.double_the_donation.doublethedonation_status,
        };
        doublethedonation.integrations.core.register_donation(ddData);
      
        this.donationWidgetState.steps.payment.isInView = false;
        this.donationWidgetState.steps.thankYou.isInView = true;
      } catch (reason) {
        console.error('Digital wallet payment failed:', reason);

        // Extract error message with fallbacks for different error formats
        let errorMessage = 'Payment failed. Please try again or use a different payment method.';

        if (reason.response?.data?.errors?.error?.[0]) {
          // Laravel validation error format
          errorMessage = reason.response.data.errors.error[0];
        } else if (reason.response?.data?.error) {
          // Direct error message format
          errorMessage = reason.response.data.error;
        } else if (reason.response?.data?.message) {
          // Generic Laravel error format
          errorMessage = reason.response.data.message;
        } else if (reason.message) {
          // JavaScript Error object
          errorMessage = reason.message;
        }

        // Always notify user of the error - critical for donation UX
        this.$notify({
          text: errorMessage,
          type: 'error',
          group: 'donationWidget',
        });
      } finally {
        // Enable the submit button
        this.isLoading = false;
      }
    },
    async next() {
      if (this.isLoading) return
      this.isLoading = true
      try {
        await this.handleCard();

        // Verify payment method was created successfully
        if (!this.paymentMethod) {
          this.$notify({
            text: 'Please check your card details and try again',
            type: 'error',
            group: 'donationWidget',
          });
          return;
        }

        this.donationWidgetState.paymentToken = this.paymentMethod.id;

        // Safely extract payment identifier
        if (this.paymentMethod.card) {
          this.donationWidgetState.paymentIdentifier = `${this.paymentMethod.card.brand || 'card'}-${this.paymentMethod.card.last4 || '****'}`;
        } else {
          this.donationWidgetState.paymentIdentifier = this.paymentMethod.type || 'unknown';
        }

        this.donationWidgetState.steps.payment.isInView = false;
        this.donationWidgetState.steps.payment.isValid = true;
        // Route to the new Double-Your-Impact step before contact.
        this.donationWidgetState.steps.employerMatch.isInView = true;
      } finally {
        this.isLoading = false
      }
    }
  }
};
</script>
<style scoped>
.dtd-search-box {
  border-radius: 0.375rem !important;
}

/* ─── Trust Panel ─────────────────────────────────────────────────
   Below the Donate button: SECURE CHECKOUT lockup + a minimal
   "— or pay with —" divider + Stripe wallet mount. The three trust
   pillars (Encrypted / Direct Impact / Deductible) live in the
   left hero panel, not here — see MakeDonationWidget.vue. */

.trust-panel {
  position: relative;
  /* Override the parent .space-y-3 sibling-margin rule (specificity
     beats plain .mt-* utilities). Larger gap above the SECURE
     CHECKOUT lockup gives the trust signal room to breathe below
     the Donate button. */
  margin-top: 1.5rem !important;
}

/* — Lockup row — small uppercase label + brand cards, both on one
   tidy line. Hairline blue-50 surface + blue-100 border anchors
   "Secure Checkout" in the brand's trust palette and balances the
   pink weight from the CTA + amount above. */
.trust-panel__lockup {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.375rem 0.625rem;
  background: #f2fafd;
  border: 1px solid #d6ecf7;
  border-radius: 0.5rem;
}

.trust-panel__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0075a7;
  white-space: nowrap;
}

.trust-panel__eyebrow-icon {
  width: 0.875rem;
  height: 0.875rem;
  color: #009cde;
  flex-shrink: 0;
}

.trust-panel__cards {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.trust-panel__card {
  height: 18px;
  width: auto;
  border-radius: 2px;
  display: block;
  /* The brand SVGs read very saturated against the blue-50 strip —
     pull saturation down ~15% so the lockup stays elegant without
     erasing brand recognition. Hover restores full color. */
  filter: saturate(0.88);
  transition: filter 200ms ease;
}

.trust-panel__lockup:hover .trust-panel__card {
  filter: saturate(1);
}

/* On the smallest devices (iPhone SE / 320px and similar) the eyebrow
   + 4 card logos plus padding need ~305px to fit on a single line. The
   modal goes full-width on mobile and the form column reserves px-10
   (40px each side), so any viewport ≤ 384px overflows the lockup with
   the cards spilling out the right edge. Wrapping to two rows
   (eyebrow on top, cards below, both centered) keeps the trust signal
   intact without breaking the rest of the layout. Threshold sits just
   below the natural overflow point so the wrap only kicks in when it
   actually needs to. */
@media (max-width: 384px) {
  .trust-panel__lockup {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 0.5rem;
  }
}

/* Mount node is always in the DOM (Stripe needs it). When the wallet
   isn't available it stays at zero height; when it is, give it a
   little breathing room above the Back link. */
.trust-panel__wallet-mount.is-active {
  margin-top: 0.125rem;
}

/* — Wallet preview placeholder — only renders under ?wallet=preview.
   Visually mimics Stripe's payment-request button (black pill, 40px
   tall, white label) so spacing can be tuned without a real Google
   Pay account in the local browser. Decorative only: pointer-events
   are disabled and the button is hidden from the accessibility tree. */
.trust-panel__wallet-preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  background: #000;
  border: 0;
  border-radius: 9999px;
  color: #fff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: default;
  pointer-events: none;
}

.trust-panel__wallet-preview-label {
  /* Intentionally empty — natural inline text rendering handles the
     "Donate with G Pay" label layout. Previous inline-flex + gap setup
     produced double-spacing artifacts around the bolded G span in some
     browsers because text nodes around the span aren't flex items. */
}

.trust-panel__wallet-preview-g {
  font-weight: 700;
}

/* Step-2 back row. The back button is the shared .dw-back-pill--lg (styled in
   MakeDonationWidget.vue); its <fieldset> uses mt-auto to sit at the card
   bottom. But mt-auto collapses to 0 when the content fills a short viewport,
   which let the pill butt right up against the wallet ("Donate with G Pay")
   button on mobile. A fixed padding-top guarantees a gap above the back button
   at any height — mt-auto still anchors it to the bottom on tall screens. */
.dw-back-row {
  padding-top: 1.5rem;
}

/* Minimal wallet divider — centered em-dashed phrase in muted gray,
   ~13px, generous vertical breathing on both sides. The only chrome
   between DONATE and the wallet button below it. */
.wallet-divider-slim {
  text-align: center;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #8B8B8B;
  letter-spacing: 0.04em;
  margin: 1.5rem 0 1rem;
}
</style>
